import { ChangeDetectionStrategy, Component, Inject, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterOutlet } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { TherapistStatusHistoryModel } from '../../models/api/TherapistStatusHistoryModel';
import { TherapistModel } from '../../models/api/TherapistModel';
import { TherapistStatusHistoryService } from '../../services/api/TherapistStatusHistoryService';
import { TherapistService } from '../../services/api/TherapistService';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { AsyncPipe, DatePipe, LocationStrategy, UpperCasePipe } from '@angular/common';
import { Observable, interval, startWith, map, Subscription } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { SharedTherapistDetailService } from '../../services/shared-therapist-detail.service'
import { TherapistDashboardService } from '../../services/api/TherapistDashboardService';
import { TherapistBreakDialogService } from '../../services/therapist-break.service';
import { MatIconModule } from '@angular/material/icon';
import { TherapistEventService } from '../../services/api/TherapistEventService';
import { TherapistEventDetailsModel } from '../../models/api/TherapistEventDetails';
import { NetworkStatusService } from '../../services/network-status.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HelpersModule } from '../../helpers/helpers';

@Component({
  selector: 'app-therapist-portal',
  standalone: true,
  imports: [RouterOutlet, MatButtonModule, MatMenuModule, MatIconModule, DatePipe],
  templateUrl: './therapist-portal.component.html',
  styleUrl: './therapist-portal.component.scss'
})
export class TherapistPortalLayoutComponent {

  therapistId: number | undefined;
  therapistStatusDetail: TherapistStatusHistoryModel = new TherapistStatusHistoryModel;
  therapistDetail: TherapistModel = new TherapistModel;
  activeTherapistStatusHistoryDetail: TherapistStatusHistoryModel = new TherapistStatusHistoryModel;
  systemUserId: number = 0;
  readonly dialog = inject(MatDialog);
  private therapistBreakDialogRef: MatDialogRef<TherapistBreakDialog> | null = null;
  private therapistClockInDialogRef: MatDialogRef<TherapistClockInDialog> | null = null;
  public acceptBookings: boolean = false;
  public therapistEventData: TherapistEventDetailsModel = new TherapistEventDetailsModel;
  private clockInDialogOpened = false;

  noEvent: boolean = false;

  isOnline: boolean = true;

  actionButtonClicked: boolean = false;

  constructor(private therapistStatusHistoryService: TherapistStatusHistoryService,
    private therapistService: TherapistService,
    private authService: AuthService,
    private therapistBreakDialogService: TherapistBreakDialogService,
    private therapistDashboardService: TherapistDashboardService,
    private therapistEventService: TherapistEventService,
    private sharedTherapistDetailService: SharedTherapistDetailService,
    private router: Router,
    private location: LocationStrategy,
    private networkStatusService: NetworkStatusService,
    private snackbar: MatSnackBar,) { }


  ngOnInit(): void {

    this.networkStatusService.status$.subscribe(status => {
      this.isOnline = status;

      if (this.isOnline) {

        const user = this.authService.getAuthUserRecordFromLocalStorage();
        if (user) {
          this.systemUserId = user.user.systemUserID;
          this.getEventData();
          this.getTherapistDetails(this.systemUserId);
        }
      }
    })

    this.sharedTherapistDetailService.showClockIn$.subscribe(() => {
      this.showTherapistClockInDialog(this.therapistDetail);
    });
  }

  ngOnDestroy() {
    if (this.therapistClockInDialogRef) {
      this.therapistClockInDialogRef.close();
    }
    if (this.therapistBreakDialogRef) {
      this.therapistBreakDialogRef.close();
    }
  }

  getEventData() {
    this.therapistEventService.getActiveByTherapist().subscribe({
      next: (data) => {
        this.therapistEventData = data;
      },
      error: (err) => {
        if (err.errorcode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorcode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorcode === 403) {
          this.snackbar.open('You are not authosied to access the current event.', 'Dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorcode === 404) {
          this.snackbar.open('Current event not found, please try again', 'dismiss');
          this.noEvent = true;
        }
        else if (err.errorcode === 400) {
          this.snackbar.open('Invalid request, please check the event and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorcode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
        }

      }
    })
  }


  getTherapistDetails(systemUserId: number) {


    this.therapistService.findTherapist(0, 0, "", "", "", systemUserId).subscribe({
      next: (data) => {
        const therapistData = data.data as unknown as TherapistModel[];
        const therapist = therapistData.find(d => d.systemUserID === systemUserId);
        if (therapist) {
          this.therapistDetail = therapist;

          if (this.therapistDetail.profileLargeURL) {
            this.therapistDetail.profileSmallURL = HelpersModule.cloudinaryURLForceWidth(this.therapistDetail.profileLargeURL!, 80);
          }

          this.therapistId = therapist.therapistID;
          this.getTherapistStatus(this.therapistId);
          this.sharedTherapistDetailService.setTherapistData(therapist);
          if (therapist.acceptBookings) {
            this.acceptBookings = true;
          }
          if (this.therapistDetail.therapistStatusID === 2 || this.therapistDetail.therapistStatusID === 5) {
            this.showTherapistClockInDialog(this.therapistDetail);
          }

        } else {
          console.warn('Therapist not found for systemUserId:', systemUserId);
        }
      },
      error: (err) => {

        if (err.errorCode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorCode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 403) {
          this.snackbar.open('You are not authosied toget the therapist details', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 404) {
          this.snackbar.open('Therapist not found, please try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode === 400) {
          this.snackbar.open('Invalid request, please check the event and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
        }

      }
    });
  }

  showTherapistClockInDialog(therapistDetail: TherapistModel) {
    if (!this.clockInDialogOpened) {
      this.therapistClockInDialogRef = this.dialog.open(TherapistClockInDialog, {
        data: {
          therapistDetail: therapistDetail
        },
        panelClass: 'therapist-portal-dialog',
        disableClose: true,
        backdropClass: 'dialog-blur',
      });
      this.clockInDialogOpened = true;

      this.therapistClockInDialogRef?.afterClosed().subscribe({
        next: (result) => {
          this.therapistDashboardService.clockIn().subscribe(data => {
            this.getTherapistDetails(this.systemUserId);
            this.clockInDialogOpened = false;
          })
        },
        error: (err) => {
          if (err.errorCode === 0) {
            this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
          }
          else if (err.errorCode === 401) {
            this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
            this.logOutUser();
          }
          else if (err.errorCode === 403) {
            this.snackbar.open('You are not authosied to clock in.', 'Dismiss');
            this.logOutUser();
          }
          else if (err.errorCode === 404) {
            this.snackbar.open('Therapist not found, please try again', 'dismiss');
            this.logOutUser();
          }
          else if (err.errorCode === 400) {
            this.snackbar.open('Invalid request, please check and try again', 'dismiss');
            this.router.navigate(['therapist-portal', 'dashboard']);
          }
          else if (err.errorCode >= 500) {
            this.snackbar.open('Server error, please try again later', 'dismiss');
            this.router.navigate(['therapist-portal', 'dashboard']);
          }
        }
      })

    }
  }


  clockOutUser() {

    this.actionButtonClicked = true;

    this.therapistDashboardService.clockOut().subscribe({
      next: (result) => {
        this.getTherapistDetails(this.systemUserId);
        this.actionButtonClicked = false;
      },
      error: (err) => {

        if (err.errorCode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorCode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 403) {
          this.snackbar.open('You are not authosied to clock out.', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 404) {
          this.snackbar.open('Therapist not found, please try again', 'dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 400) {
          this.snackbar.open('Invalid request, please check and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }

        this.actionButtonClicked = false;

      }
    })

  }

  startBreak() {
    this.actionButtonClicked = true;
    this.therapistDashboardService.startBreak().subscribe({
      next: (data) => {
        this.getTherapistDetails(this.systemUserId);
        this.actionButtonClicked = false;

      },
      error: (err) => {


        if (err.errorCode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorCode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 403) {
          this.snackbar.open('You are not authosied to start a break.', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 404) {
          this.snackbar.open('Current break not found, please try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode === 400) {
          this.snackbar.open('Invalid request, please check and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }


        this.actionButtonClicked = false;


      }
    })
  }


  endBreak() {
    this.actionButtonClicked = true;

    this.therapistDashboardService.endBreak().subscribe({
      next: (data) => {
        this.getTherapistDetails(this.systemUserId);
        this.actionButtonClicked = false;

      },
      error: (err) => {


        if (err.errorCode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorCode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 403) {
          this.snackbar.open('You are not authosied to end a break.', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 404) {
          this.snackbar.open('Current break not found, please try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode === 400) {
          this.snackbar.open('Invalid request, please check and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }


        this.actionButtonClicked = false;
      }
    })
  }

  async logOutUser() {
    this.router.navigate(['login']);
    this.authService.logout().subscribe({});

  }


  startBookings() {

    this.actionButtonClicked = true;

    this.therapistDashboardService.setAcceptBookings(true).subscribe({
      next: (data) => {
        this.acceptBookings = true;
        this.getTherapistDetails(this.systemUserId);
        this.actionButtonClicked = false;
      },
      error: (err) => {


        if (err.errorCode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorCode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 403) {
          this.snackbar.open('You are not authosied to start acceptning bookings.', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 404) {
          this.snackbar.open('Current bookings not found, please try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode === 400) {
          this.snackbar.open('Invalid request, please check and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }


        this.actionButtonClicked = false;
      }
    })
  }

  stopBookings() {

    this.actionButtonClicked = true;

    this.therapistDashboardService.setAcceptBookings(false).subscribe({
      next: (data) => {
        this.acceptBookings = false;
        this.getTherapistDetails(this.systemUserId);
        this.actionButtonClicked = false;
      },
      error: (err) => {


        if (err.errorCode === 0) {
          this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
        }
        else if (err.errorCode === 401) {
          this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 403) {
          this.snackbar.open('You are not authosied to stop taking bookings.', 'Dismiss');
          this.logOutUser();
        }
        else if (err.errorCode === 404) {
          this.snackbar.open('Current bookings not found, please try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode === 400) {
          this.snackbar.open('Invalid request, please check and try again', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }
        else if (err.errorCode >= 500) {
          this.snackbar.open('Server error, please try again later', 'dismiss');
          this.router.navigate(['therapist-portal', 'dashboard']);
        }

        this.actionButtonClicked = false;

      }
    })
  }


  getTherapistStatus(therapistId: number) {
    this.activeTherapistStatusHistoryDetail = new TherapistStatusHistoryModel;

    this.therapistStatusHistoryService
      .findTherapistStatusHistory(0, 1, "ChangeDateTime", "DESC", "", therapistId, 0).subscribe({
        next: (response) => {
          this.activeTherapistStatusHistoryDetail = response?.data?.[0] as TherapistStatusHistoryModel ?? null;

          if (this.activeTherapistStatusHistoryDetail) {
            const changeDate = new Date(this.activeTherapistStatusHistoryDetail.changeDateTime);
          }
        },
        error: (err) => {
          if (err.errorCode === 0) {
            this.snackbar.open('Network error. Please check your internet connection and try again', 'Dismiss');
          }
          else if (err.errorCode === 401) {
            this.snackbar.open('Your sesison has expired. Please log in and try again', 'Dismiss');
            this.logOutUser();
          }
          else if (err.errorCode === 403) {
            this.snackbar.open('You are not authosied to get the therapist details', 'Dismiss');
            this.logOutUser();
          }
          else if (err.errorCode === 404) {
            this.snackbar.open('Current therapist not found, please try again', 'dismiss');
            this.router.navigate(['therapist-portal', 'dashboard']);
          }
          else if (err.errorCode === 400) {
            this.snackbar.open('Invalid request, please check and try again', 'dismiss');
            this.router.navigate(['therapist-portal', 'dashboard']);
          }
          else if (err.errorCode >= 500) {
            this.snackbar.open('Server error, please try again later', 'dismiss');
            this.router.navigate(['therapist-portal', 'dashboard']);
          }
        }
      })

  }
  goBack() {
    this.location.back();
  }
}

@Component({
  selector: 'therapist-break-dialog',
  templateUrl: '../../therapist-poral/dashboard/therapist-break-dialog.html',
  standalone: true,
  imports: [MatButtonModule, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TherapistBreakDialog implements OnInit {
  timeSinceStatusChange!: Observable<number>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { changeDate: Date },
    private dialogRef: MatDialogRef<TherapistBreakDialog>
  ) { }

  ngOnInit(): void {
    this.timeSinceStatusChange = interval(60000).pipe(
      startWith(0),
      map(() => this.calculateMinutesSince(this.data.changeDate))
    );
  }

  private calculateMinutesSince(date: Date): number {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();

    //Remove 59 mins due to time difference in DB > adding an extra min so first minute always shows as 1.
    return Math.floor(diffInMs / 60000); // minutes
  }
}

@Component({
  selector: 'therapist-clockin-dialog',
  templateUrl: '../../therapist-poral/dashboard/therapist-clockin-dialog.html',
  standalone: true,
  imports: [MatButtonModule, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent, UpperCasePipe],
})


export class TherapistClockInDialog {
  timeInHours = Math.round(new Date().getHours());

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { therapistDetail: TherapistModel },
    private dialogRef: MatDialogRef<TherapistClockInDialog>) { }
}
